import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  portalDs,
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import {
  ClientExclusion,
  ClientExclusionType,
  useCreateClientExclusionMutation,
  useDeleteClientExclusionMutation,
} from 'src/pages/client-management/endpoints/client-exclusions.endpoints';

interface ExclusionListSectionProps {
  title: string;
  type: ClientExclusionType;
  entries: ClientExclusion[];
  isLoading: boolean;
}

const ExclusionListSection: React.FC<ExclusionListSectionProps> = ({
  title,
  type,
  entries,
  isLoading,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [pendingRemove, setPendingRemove] = useState<ClientExclusion | null>(null);
  const [createExclusion, { isLoading: isCreating }] = useCreateClientExclusionMutation();
  const [deleteExclusion, { isLoading: isDeleting }] = useDeleteClientExclusionMutation();

  const handleAdd = async() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    try {
      await createExclusion({ type, value: trimmed }).unwrap();
      setInputValue('');
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleConfirmRemove = async() => {
    if (!pendingRemove) return;

    try {
      await deleteExclusion(pendingRemove.id).unwrap();
      setPendingRemove(null);
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  return (
    <section className={cn(portalDs.card, portalDs.cardPadding)}>
      <h2 className={cn(portalDs.headingH3, 'mb-4')}>
        {title}
      </h2>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <PortalDsField
          label={`Add ${type}`}
          filter
          className="min-w-0 flex-1"
        >
          <PortalDsInput
            value={inputValue}
            placeholder={`Add ${type}`}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleAdd();
              }
            }}
          />
        </PortalDsField>
        <PortalDsButton
          type="button"
          variant="primary"
          size="md"
          disabled={!inputValue.trim() || isCreating}
          onClick={() => void handleAdd()}
        >
          {isCreating ? (
            <>
              <Loader2
                className="size-4 animate-spin"
                aria-hidden
              />
              Adding…
            </>
          ) : (
            'Add'
          )}
        </PortalDsButton>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-[#5E6278]">
          <Loader2
            className="size-4 animate-spin"
            aria-hidden
          />
          <span className={portalDs.bodySm}>
            Loading exclusions…
          </span>
        </div>
      ) : entries.length === 0 ? (
        <p className={portalDs.bodySm}>
          No exclusions configured.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-[#EDEEF2]">
          {entries.map((entry, index) => (
            <li
              key={entry.id}
              className={cn(
                'flex items-center justify-between gap-4 px-4 py-3',
                index > 0 && 'border-t border-[#EDEEF2]',
                'hover:bg-[#F0F1F5]'
              )}
            >
              <span className="break-all text-[13.5px] text-[#0D0F1A]">
                {entry.value}
              </span>
              <PortalDsButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={isDeleting}
                className="shrink-0 border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
                onClick={() => setPendingRemove(entry)}
              >
                Remove
              </PortalDsButton>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={pendingRemove !== null}
        onOpenChange={(open) => !open && setPendingRemove(null)}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Remove exclusion?
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {pendingRemove
                ? `Remove "${pendingRemove.value}" from excluded ${type}s? Client reports will include this ${type} again immediately.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => setPendingRemove(null)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              disabled={isDeleting}
              className="border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
              onClick={() => void handleConfirmRemove()}
            >
              {isDeleting ? 'Removing…' : 'Remove'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ExclusionListSection;
